<template>
    <div v-click-outside="hide" class="w-full relative">
        <label
            v-if="label"
            class="block mb-1 text-sm text-theme-muted font-semibold"
        >
            {{ label }}
        </label>
        <div class="relative flex items-center">
            <span
                class="absolute left-3 flex items-center justify-center text-theme-muted cursor-pointer"
                @click="triggerImmediateSearch"
            >
                <slot name="icon">🔎</slot>
            </span>
            <input
                v-model="query"
                type="text"
                autocomplete="off"
                :placeholder="placeholder"
                :disabled="disabled || disabledInput"
                class="w-full rounded-md border border-theme-border bg-theme-input text-theme-base pl-9 pr-3 py-2 text-sm transition-colors focus:outline-none focus:ring-1 focus:ring-theme-primary"
                :class="
                    disabled || disabledInput
                        ? 'opacity-80 cursor-not-allowed text-theme-disabled'
                        : ''
                "
                @input="onInput"
                @focus="onFocus"
                @blur="$emit('blur')"
                @keyup.esc="hide"
                @keyup.enter="onEnter"
                @keyup.down="incrementFocus"
                @keyup.up="decrementFocus"
            />
        </div>
        <div
            v-if="show"
            class="absolute z-600 mt-1 w-full rounded-md border border-theme-border bg-theme-card shadow-lg"
        >
            <div class="max-h-72 overflow-auto">
                <div
                    v-if="isLoading"
                    class="px-3 py-2 text-sm text-theme-muted"
                >
                    Cargando...
                </div>
                <template v-else>
                    <button
                        v-for="(item, index) in items"
                        :key="item[valueKey]"
                        type="button"
                        class="w-full text-left px-3 py-2 text-sm text-theme-base hover:bg-theme-hover"
                        :class="{ 'bg-theme-hover': index === focusIndex }"
                        @click="selectItem(item)"
                    >
                        <slot name="item" :item="item">
                            {{ item[labelKey] }}
                        </slot>
                    </button>
                    <div
                        v-if="!items.length"
                        class="px-3 py-2 text-sm text-theme-muted"
                    >
                        Sin resultados
                    </div>
                </template>
            </div>
            <slot name="footer" />
        </div>
    </div>
</template>

<script>
import ClickOutside from "vue-click-outside";
import { mapGetters } from "vuex";

export default {
    name: "CustomSearch",
    directives: {
        ClickOutside
    },
    props: {
        fetchItems: {
            type: Function,
            required: true
        },
        labelKey: {
            default: () => "name",
            type: String,
            required: false
        },
        valueKey: {
            default: () => "id",
            type: String,
            required: false
        },
        label: {
            default: () => "",
            type: String,
            required: false
        },
        placeholder: {
            default: () => "Buscar...",
            type: String,
            required: false
        },
        disabled: {
            default: () => false,
            type: Boolean,
            required: false
        },
        debounceMs: {
            default: () => 400,
            type: Number,
            required: false
        },
        displayValue: {
            default: () => "",
            type: String,
            required: false
        },
        showItemsOnFocus: {
            default: () => false,
            type: Boolean,
            required: false
        }
    },
    emits: ["select", "focus", "blur"],
    data: () => ({
        query: "",
        items: [],
        isLoading: false,
        show: false,
        focusIndex: -1,
        debounceTimeout: null,
        selectedLabel: "",
        disabledInput: false
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {
        displayValue(value) {
            this.setDisplayValue(value);
        }
    },
    mounted() {
        this.setDisplayValue(this.displayValue);
    },
    methods: {
        setDisplayValue(value) {
            this.query = value || "";
            this.selectedLabel = value || "";
        },
        onFocus() {
            if (this.query === this.selectedLabel) {
                this.query = "";
            }
            if (this.showItemsOnFocus && !this.query) {
                this.show = true;
                this.search(false);
            }
            this.$emit("focus");
        },
        onInput() {
            this.focusIndex = -1;
            clearTimeout(this.debounceTimeout);
            if (!this.query) {
                if (this.showItemsOnFocus) {
                    this.show = true;
                    this.search(false);
                } else {
                    this.show = false;
                    this.items = [];
                }
                return;
            }
            this.show = true;
            this.debounceTimeout = setTimeout(
                () => this.search(false),
                this.debounceMs
            );
        },
        triggerImmediateSearch() {
            if (!this.query) return;
            this.show = true;
            clearTimeout(this.debounceTimeout);
            this.search(true);
        },
        async search(required) {
            this.isLoading = true;
            try {
                this.items = await this.fetchItems(this.query, required);
                this.focusIndex = -1;
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        selectItem(item) {
            this.$emit("select", item);
            this.selectedLabel = item[this.labelKey];
            this.query = item[this.labelKey];
            this.show = false;
            this.focusIndex = -1;
            this.disabledInput = true;
            setTimeout(() => {
                this.disabledInput = false;
            }, 300);
        },
        hide() {
            this.query = this.selectedLabel;
            this.show = false;
            this.focusIndex = -1;
        },
        onEnter() {
            if (this.focusIndex > -1) {
                this.selectItem(this.items[this.focusIndex]);
            } else {
                this.triggerImmediateSearch();
            }
        },
        incrementFocus() {
            if (this.focusIndex === -1) {
                this.focusIndex = 0;
            } else if (this.focusIndex < this.items.length - 1) {
                this.focusIndex = this.focusIndex + 1;
            }
        },
        decrementFocus() {
            if (this.focusIndex === -1) {
                this.focusIndex = 0;
            } else if (this.focusIndex > 0) {
                this.focusIndex = this.focusIndex - 1;
            }
        }
    }
};
</script>
